import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Tabs, Tab } from "react-bootstrap";
import DatePicker from "react-datepicker";
import moment from "moment-timezone";

import { dirname } from "../../../util/resourcePath";
import GYAxios from "../../../util/GYAxios";

import DefaultSelect from "../../general/DefaultSelect";
import ReceiptTable from "./ReceiptTable";

import "../admin.css";
import {
	localTimeToPickerTime,
	pickerTimeTolocalTime,
} from "../../../util/momentTimezone";

export const ReceiptContent = () => {
	const directory = dirname();
	const [currentTab, setCurrentTab] = useState("PENDING");
	const [receipts, setReceipts] = useState([]);
	const [pendingReceipts, setPendingReceipts] = useState([]);
	const [paidReceipts, setPaidReceipts] = useState([]);
	const [outstandingCount, setOutstandingCount] = useState(0);
	const [reload, setReload] = useState(true);

	const defaultMonth = moment().subtract(1, "month");
	const [filterCategory, setFilterCategory] = useState("dateData");
	const [filterOptions, setFilterOptions] = useState([]);
	const [filterValue, setFilterValue] = useState(defaultMonth.format("YYYYMM"));
	const [isLoadingFilters, setIsLoadingFilters] = useState(false);
	const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
	const [filter, setFilter] = useState({ prop: "dateData", value: defaultMonth.format("YYYYMM") });
	const [selectedRows, setSelectedRows] = useState([]);
	const [downloadButton, setDownloadButton] = useState(null);
	const isInitialMount = useRef(true);

	const minDate = moment("202103", "YYYYMM");
	const maxDate = moment().subtract(1, "month");

	// Select filters
	const values = [
		{
			label: "Select a filter",
			value: "",
		},
		{
			label: "Entity",
			value: "entity_id",
		},
		{
			label: "Client",
			value: "client_id",
		},
		{
			label: "Month",
			value: "dateData",
		},
		{
			label: "Invoice No.",
			value: "invoice_no",
		},
		{
			label: "MSA",
			value: "site_id",
		},
	];

	/**
	 * Fetch all receipts and pending receipts
	 */
	const fetchReceipts = async () => {
		let column = filter.prop || null;
		let value = filter.value || null;
		let mode = "eq";

		if (filter.prop === "invoice_no") {
			mode = "like";
		} else if (filter.prop === "entity_id" && filter.value === "TH") {
			column = "operation_status";
			value = "2";
		}

		const body = {};
		if (column && value) {
			body.column = column;
			body.value = value;
			body.mode = mode;
		}

		await GYAxios.post(
			"/receipts/overview",
			body,
			{ timeout: 3 * 60 * 1000 }
		)
			.then((res) => {
				const response = res.data;
				if (!response.error) {
					const allReceipts = response.receipts || [];
					setReceipts(allReceipts);
					setPendingReceipts(response.pendingReceipts || []);
					setPaidReceipts(response.paidReceipts || []);
					setOutstandingCount(response.outstandingCount || 0);
				} else {
					setReceipts([]);
					setPendingReceipts([]);
					setPaidReceipts([]);
					setOutstandingCount(0);
				}
			})
			.catch(() => {
				setReceipts([]);
				setPendingReceipts([]);
				setPaidReceipts([]);
				setOutstandingCount(0);
			})
			.finally(() => {
				setReload(false);
			});
	};

	/**
	 * Fetch selected filter rows
	 * @param {string} category
	 */
	const fetchFilterOptions = (category) => {
		if (category != "") {
			let uri = "";
			switch (category) {
				case "entity_id":
					uri = "/select/entities/TH";
					break;
				case "client_id":
					uri = "/select/clients/TH";
					break;
				case "invoice_no":
					uri = "/select/invoice-no";
					break;
				case "site_id":
					uri = "/select/site/msa";
					break;
				default:
					break;
			}

			if (uri != "") {
				setIsLoadingFilters(true);

				GYAxios.get(uri).then((res) => {
					const response = res.data;
					if (!response.error) {
						let opts = [];

						if (category === "invoice_no") {
							const temp = [...response].map((obj) => {
								const invoiceNo = obj.invoice_no;

								const numberSplit = invoiceNo.split("-");

								// Remove date and document number of invoice
								numberSplit.splice(numberSplit.length - 2, 2);

								const numberString = `${numberSplit.join("-")}-`;

								return numberString;
							});

							const uniqueArr = [...new Set(temp)];
							opts = uniqueArr.map((invoice_no) => ({
								label: invoice_no,
								value: invoice_no,
							}));
						} else if (category === "site_id") {
							const checkApprovalStat = (obj) => {
								if (obj.approvalStatus === 3) {
									return obj;
								}
							};
							let temp = response.filter((obj) => checkApprovalStat(obj));
							let selections = [];
							temp.forEach((obj) => {
								if (obj.company === obj.site_name) {
									selections.push({ label: obj.company, value: obj.site_id });
								} else {
									selections.push({
										label: obj.company + " - " + obj.site_name,
										value: obj.site_id,
									});
								}
							});
							opts = [
								...new Map(selections.map((opt) => [opt.value, opt])).values(),
							];
						} else {
							opts = response.map((opt) => ({
								label: opt.label,
								value: opt.value,
							}));
						}
						setFilterOptions(opts);
					}
					setIsLoadingFilters(false);
				});
			}
		} else resetFilter();
	};

	/**
	 * Reset filters
	 */
	const resetFilter = () => {
		setFilterOptions([]);
		setFilterValue("");
		setFilter({ prop: "", value: "" });
		setReload(true);
	};

	/**
	 * Handle change of month in month picker
	 * @param {Date} date
	 */
	const handleMonthChange = (date) => {
		const dateobj = moment(date);

		setSelectedMonth(dateobj);
		setFilterValue(dateobj.format("YYYYMM"));
	};

	/**
	 * Handle filter category change
	 * @param {object} obj
	 */
	const handleFilterChange = (obj) => {
		setFilterCategory(obj.value);
		setFilterValue("");
	};

	useEffect(() => {
		if (reload) fetchReceipts();
	}, [reload]);

	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
		} else {
			setSelectedMonth(null);
			setFilterValue("");
			setSelectedRows([]);
		}
		fetchFilterOptions(filterCategory);
	}, [filterCategory]);

	useEffect(() => {
		setSelectedRows([]);
	}, [currentTab]);

	useEffect(() => {
		if (filterCategory != "" && filterValue != "") {
			setFilter({
				prop: filterCategory,
				value: filterValue,
			});
			setReload(true);
		} else if (filterCategory === "") {
			// "Select a filter" chosen — fetch all invoices
			setFilter({ prop: "", value: "" });
			setReload(true);
		}
	}, [filterCategory, filterValue]);

	return (
		<div className="perf-table panel panel-default">
			<div className="panel-heading ">
				<Row>
					<Col sm="6" md="6" lg="6">
						<h2 className="panel-title">Receipts Overview</h2>
					</Col>
					<Col sm="6" md="6" lg="6" style={{ textAlign: "right" }}>
						<img
							src={`${directory}/images/reload.png`}
							onClick={() => setReload(true)}
							style={{
								height: "35px",
								cursor: "pointer",
							}}
							alt="logo"
						/>
					</Col>
				</Row>
				<Row>
					<Col md="6">{downloadButton}</Col>
					<Col md="3">
						<DefaultSelect
							value={values.find((v) => v.value === filterCategory) || values[0]}
							options={values}
							customStyle={{
								width: "100%",
							}}
							onChange={handleFilterChange}
						/>
					</Col>
					<Col md="3">
						{filterCategory === "dateData" ? (
							<div id="receipt-month-selector">
								<DatePicker
									minDate={localTimeToPickerTime(minDate.toDate())}
									maxDate={localTimeToPickerTime(maxDate.toDate())}
									value={
										selectedMonth != null
											? selectedMonth.format("MMMM yyyy")
											: "Select a month.."
									}
									id="receipt-date"
									onChange={(date) =>
										handleMonthChange(pickerTimeTolocalTime(date))
									}
									selected={
										selectedMonth != null
											? localTimeToPickerTime(selectedMonth.toDate())
											: null
									}
									dateFormat="MMMM yyyy"
									showMonthYearPicker
								/>
							</div>
						) : (
							<DefaultSelect
								options={filterOptions}
								isLoading={isLoadingFilters}
								onChange={(obj) => {
									setFilterValue(obj.value);
								}}
								customStyle={{
									width: "100%",
								}}
							/>
						)}
					</Col>
				</Row>
			</div>
			<div id="receipt-container">
				<Tabs
					id="receipt-component"
					className="receipt-tabs"
					activeKey={currentTab}
					onSelect={(k) => setCurrentTab(k)}
				>
					<Tab
						eventKey="PENDING"
						title={
							<span>
								Pending
								<span id="counter">
									&nbsp;<b>{outstandingCount}</b>
								</span>
							</span>
						}
					>
						<ReceiptTable
							tab="PENDING"
							data={pendingReceipts}
							reload={reload}
							setReload={setReload}
							selectedRows={selectedRows}
							setSelectedRows={setSelectedRows}
							setDownloadButton={setDownloadButton}
						/>
					</Tab>
					<Tab
						eventKey="PAID"
						title={
							<span>
								Paid
								<span id="counter">
									&nbsp;<b>{paidReceipts.length}</b>
								</span>
							</span>
						}
					>
						<ReceiptTable
							tab="PAID"
							data={paidReceipts}
							reload={reload}
							setReload={setReload}
							selectedRows={selectedRows}
							setSelectedRows={setSelectedRows}
							setDownloadButton={setDownloadButton}
						/>
					</Tab>
					<Tab eventKey="ALL" title="All">
						<ReceiptTable
							tab="ALL"
							data={receipts}
							reload={reload}
							setReload={setReload}
							selectedRows={selectedRows}
							setSelectedRows={setSelectedRows}
							setDownloadButton={setDownloadButton}
						/>
					</Tab>
				</Tabs>
			</div>
		</div>
	);
};
